import { forEach, isBoolean, isPromise, pipe } from 'remeda'
import { composeCleanup } from '../helpers/cleanup'
import { isElementDisabled } from '../helpers/components'
import { bindLiveProps, type LivePropValues } from '../helpers/live-props'
import {
  setupVisibilityCommands,
  type VisibilityHookContext
} from '../helpers/visibility'
import { setupDropdownSearch } from './dropdown-search'

// Private types ---------------------------------------------------------------

type DropdownElement = HTMLElement & {
  open: boolean
}

type UpdatableDropdownElement = DropdownElement & {
  readonly updateComplete: Promise<unknown>
}

type ActiveDropdownItem = HTMLElement & {
  active: boolean
}

type CommandState = Readonly<{
  active: boolean
  desiredOpen: boolean
  hiding: boolean
  reopening: boolean
  showDuringHide: boolean
}>

// Public types ----------------------------------------------------------------

export type DropdownHookContext = VisibilityHookContext & {
  dropdownController?: DropdownController
}

export type DropdownController = Readonly<{
  cleanup: () => void
  prepareForUpdate: () => void
  sync: () => void
}>

// Constants -------------------------------------------------------------------

const EMPTY_DROPDOWN_CONTROLLER: DropdownController = {
  cleanup: () => undefined,
  prepareForUpdate: () => undefined,
  sync: () => undefined
}

// Private functions -----------------------------------------------------------

// Narrows a hook host to the Web Awesome dropdown API.
const isDropdownElement = (element: HTMLElement): element is DropdownElement =>
  element.tagName === 'WA-DROPDOWN' &&
  'open' in element &&
  isBoolean(element.open)

// Narrows a dropdown that exposes an async update boundary.
const isUpdatableDropdownElement = (
  element: DropdownElement
): element is UpdatableDropdownElement =>
  'updateComplete' in element && isPromise(element.updateComplete)

// Reads whether selection should keep this dropdown open.
const staysOpenOnSelect = (dropdown: HTMLElement): boolean =>
  dropdown.dataset.stayOpenOnSelect === 'true'

// Finds the direct dropdown item that owns an interaction.
const eventDropdownItem = (
  dropdown: HTMLElement,
  event: Event
): HTMLElement | undefined => {
  const target = event.target
  const item =
    target instanceof HTMLElement ? target.closest('wa-dropdown-item') : null

  return item instanceof HTMLElement && item.closest('wa-dropdown') === dropdown
    ? item
    : undefined
}

// Checks whether an item opens a nested submenu.
const hasSubmenu = (item: HTMLElement): boolean =>
  ('hasSubmenu' in item && item.hasSubmenu === true) ||
  item.querySelector(':scope > wa-dropdown-item[slot="submenu"]') !== null

// Checks whether keyboard input came from the dropdown trigger.
const isTriggerEvent = (
  dropdown: HTMLElement,
  event: KeyboardEvent
): boolean => {
  const target = event.target
  const trigger = dropdown.querySelector<HTMLElement>(
    ':scope > [slot="trigger"]'
  )

  return target instanceof Node && trigger?.contains(target) === true
}

// Finds the dropdown's direct trigger element.
const dropdownTrigger = (dropdown: HTMLElement): HTMLElement | null =>
  dropdown.querySelector<HTMLElement>(':scope > [slot="trigger"]')

// Narrows an item that exposes Web Awesome's active property.
const isActiveDropdownItem = (
  element: Element
): element is ActiveDropdownItem =>
  element.tagName === 'WA-DROPDOWN-ITEM' &&
  'active' in element &&
  isBoolean(element.active)

// Keeps the roving tab stop aligned with the focused item.
const syncActiveItem = (
  dropdown: HTMLElement,
  focused: Element | null
): void => {
  pipe(
    Array.from(dropdown.querySelectorAll('wa-dropdown-item')),
    forEach((item) => {
      if (isActiveDropdownItem(item)) {
        item.active = item === focused
      }
    })
  )
}

// Restores active-item and focus state after a LiveView patch.
const reconcileActiveItem = (dropdown: DropdownElement): void => {
  const focused = document.activeElement

  if (
    !(
      focused instanceof HTMLElement &&
      isActiveDropdownItem(focused) &&
      dropdown.contains(focused)
    )
  ) {
    if (
      dropdown.open &&
      !staysOpenOnSelect(dropdown) &&
      !(focused instanceof Node && dropdown.contains(focused))
    ) {
      dropdown.open = false
      dropdownTrigger(dropdown)?.focus({ preventScroll: true })
    }

    return
  }

  syncActiveItem(dropdown, focused)
}

// Bridges Enter and Space to one native item click.
const bridgeKeyboardActivation = (
  dropdown: DropdownElement,
  event: KeyboardEvent
): void => {
  if (
    !dropdown.open ||
    event.defaultPrevented ||
    isTriggerEvent(dropdown, event)
  ) {
    return
  }

  const item = eventDropdownItem(dropdown, event)

  if (item === undefined) {
    return
  }

  if (!(event.key === 'Enter' || event.key === ' ')) {
    return
  }

  if (event.repeat) {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  if (isElementDisabled(item) || hasSubmenu(item)) {
    return
  }

  event.preventDefault()
  event.stopPropagation()
  item.click()
}

// Registers one event listener and returns its cleanup.
const listen = (
  target: EventTarget,
  type: string,
  listener: EventListener,
  capture = false
): (() => void) => {
  target.addEventListener(type, listener, capture)

  return () => target.removeEventListener(type, listener, capture)
}

// Prevents deferred submenu entry from undoing navigation or dismissal.
const setupSubmenuFocus = (dropdown: DropdownElement): (() => void) => {
  let interaction: Event | undefined

  // Retains the input event so synchronous native navigation remains authoritative.
  const rememberInteraction = (event: Event): void => {
    interaction = event
  }

  // Restores focus when submenu autofocus repeats after the user has moved on.
  const handleFocusIn = (event: FocusEvent): void => {
    const item = event.target
    const previous = event.relatedTarget

    if (
      !(
        item instanceof HTMLElement &&
        isActiveDropdownItem(item) &&
        previous instanceof HTMLElement &&
        previous.isConnected
      ) ||
      item.slot !== 'submenu' ||
      item.closest('wa-dropdown') !== dropdown
    ) {
      return
    }

    const parent = item.parentElement
    const first = Array.from(
      parent?.querySelectorAll<HTMLElement>(
        ':scope > wa-dropdown-item[slot="submenu"]'
      ) ?? []
    ).find((child) => !isElementDisabled(child))
    const followsInput =
      interaction !== undefined &&
      (interaction.eventPhase !== Event.NONE ||
        (interaction.type === 'pointerdown' &&
          eventDropdownItem(dropdown, interaction) === item))
    const returnsToParent =
      item.contains(previous) &&
      'submenuOpen' in item &&
      item.submenuOpen === false

    if (
      item !== first ||
      parent === null ||
      followsInput ||
      (dropdown.open &&
        'submenuOpen' in parent &&
        parent.submenuOpen === true &&
        (previous === parent || !parent.contains(previous) || returnsToParent))
    ) {
      return
    }

    previous.focus({ preventScroll: true })
    syncActiveItem(dropdown, dropdown.ownerDocument.activeElement)
  }

  return composeCleanup(
    listen(dropdown, 'keydown', rememberInteraction, true),
    listen(dropdown, 'pointerdown', rememberInteraction, true),
    listen(dropdown, 'focusin', handleFocusIn, true)
  )
}

// Creates the controller for one valid dropdown element.
const createDropdownController = (
  context: DropdownHookContext,
  dropdown: DropdownElement
): DropdownController => {
  const searchController = setupDropdownSearch(dropdown)
  const cleanupSubmenuFocus = setupSubmenuFocus(dropdown)
  let reconcileFrame: number | undefined
  let openingFromEnd = false
  let state: CommandState = {
    active: true,
    desiredOpen: dropdown.open,
    hiding: false,
    reopening: false,
    showDuringHide: false
  }

  // Applies desired visibility and records show-during-hide races.
  const setOpen = (open: boolean): void => {
    state = {
      ...state,
      desiredOpen: open,
      showDuringHide: open && state.hiding
    }

    // Reopening before the popup finishes hiding skips Web Awesome's focus setup.
    if (!(open && state.hiding)) {
      dropdown.open = open
    }
  }

  // Completes a hide-then-reopen transition.
  const finishReopen = (): void => {
    state = {
      ...state,
      hiding: false,
      reopening: false,
      showDuringHide: false
    }

    if (state.active && state.desiredOpen) {
      dropdown.open = true
    }
  }

  // Restarts opening only after the current hide has rendered.
  const restartAfterHide = (): void => {
    state = { ...state, reopening: true }
    dropdown.open = false

    if (isUpdatableDropdownElement(dropdown)) {
      dropdown.updateComplete.then(finishReopen)
    } else {
      queueMicrotask(finishReopen)
    }
  }

  // Records a component-originated show request.
  const handleWaShow = (event: Event): void => {
    if (event.target !== dropdown) {
      return
    }

    state = {
      ...state,
      desiredOpen: true,
      showDuringHide: state.hiding ? true : state.showDuringHide
    }

    if (state.hiding) {
      event.preventDefault()
    }
  }

  // Records the start of a component-originated hide.
  const handleWaHide = (event: Event): void => {
    if (event.target !== dropdown || state.reopening) {
      return
    }

    state = {
      ...state,
      desiredOpen:
        dropdown.open || state.showDuringHide ? state.desiredOpen : false,
      hiding: true
    }
  }

  // Focuses search or re-closes a stale completed show.
  const handleWaAfterShow = (event: Event): void => {
    if (event.target !== dropdown) {
      return
    }

    if (state.desiredOpen) {
      searchController.focus()
      if (openingFromEnd) {
        openingFromEnd = false
        const items = dropdown.querySelectorAll<HTMLElement>(
          ':scope > wa-dropdown-item:not([disabled])'
        )
        const lastItem = items.item(items.length - 1)
        lastItem?.focus({ preventScroll: true })
        syncActiveItem(dropdown, lastItem)
      }
      return
    }

    dropdown.open = false
    dropdownTrigger(dropdown)?.focus({ preventScroll: true })
  }

  // Completes hiding or restarts a queued show.
  const handleWaAfterHide = (event: Event): void => {
    if (event.target !== dropdown || state.reopening) {
      return
    }

    if (state.showDuringHide && state.desiredOpen) {
      restartAfterHide()
      return
    }

    state = { ...state, hiding: false }
    searchController.reset()
  }

  // Cancels dismissal for dropdowns that declare stay-open selection.
  const handleWaSelect = (event: Event): void => {
    if (event.target === dropdown && staysOpenOnSelect(dropdown)) {
      event.preventDefault()
    }
  }

  // Sends keyboard activation through the shared bridge.
  const handleKeydown = (event: KeyboardEvent): void => {
    if (
      isTriggerEvent(dropdown, event) &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp')
    ) {
      event.preventDefault()
      event.stopPropagation()
      openingFromEnd = event.key === 'ArrowUp'
      setOpen(true)
      return
    }

    bridgeKeyboardActivation(dropdown, event)
  }

  // Coalesces active-item repair into one animation frame.
  const scheduleActiveItemReconciliation = (): void => {
    if (reconcileFrame !== undefined) {
      cancelAnimationFrame(reconcileFrame)
    }

    reconcileFrame = requestAnimationFrame(() => {
      reconcileFrame = undefined
      reconcileActiveItem(dropdown)
    })
  }

  const cleanupCommands = setupVisibilityCommands(context, 'dropdown', {
    hide: () => setOpen(false),
    show: () => setOpen(true)
  })
  const cleanupLiveProps = bindLiveProps(dropdown, {
    read: (properties): LivePropValues =>
      properties.includes('open') ? { open: dropdown.open } : {},
    apply: (values) => {
      if (isBoolean(values.open)) {
        setOpen(values.open)
      }
    }
  })
  const cleanupListeners = composeCleanup(
    listen(dropdown, 'wa-show', handleWaShow),
    listen(dropdown, 'wa-after-show', handleWaAfterShow),
    listen(dropdown, 'wa-hide', handleWaHide),
    listen(dropdown, 'wa-after-hide', handleWaAfterHide),
    listen(dropdown, 'wa-select', handleWaSelect),
    listen(dropdown, 'keydown', handleKeydown)
  )

  return {
    cleanup: () => {
      state = { ...state, active: false }
      dropdown.open = false
      cleanupLiveProps()

      if (reconcileFrame !== undefined) {
        cancelAnimationFrame(reconcileFrame)
      }

      cleanupCommands()
      cleanupSubmenuFocus()
      searchController.cleanup()
      cleanupListeners()
    },
    prepareForUpdate: searchController.prepareForUpdate,
    sync: () => {
      if (!state.active) {
        return
      }

      searchController.sync()
      scheduleActiveItemReconciliation()
    }
  }
}

// Public functions ------------------------------------------------------------

// Creates a safe dropdown controller for any hook host.
export const setupDropdown = (
  context: DropdownHookContext
): DropdownController =>
  isDropdownElement(context.el)
    ? createDropdownController(context, context.el)
    : EMPTY_DROPDOWN_CONTROLLER

// Public hook -----------------------------------------------------------------

export const FacadeDropdown = {
  // Creates the dropdown controller.
  mounted(this: DropdownHookContext) {
    this.dropdownController = setupDropdown(this)
  },

  // Preserves search state before LiveView changes the DOM.
  beforeUpdate(this: DropdownHookContext) {
    this.dropdownController?.prepareForUpdate()
  },

  // Reconciles search, focus, and active-item state after a patch.
  updated(this: DropdownHookContext) {
    this.dropdownController?.sync()
  },

  // Releases every dropdown-owned resource.
  destroyed(this: DropdownHookContext) {
    this.dropdownController?.cleanup()
    this.dropdownController = undefined
  }
}
